import { useState, useEffect } from "react";
import api from "../services/api";
import { Box, Paper, Stack, Typography, TextField, Button, InputAdornment, IconButton, Alert, MenuItem, Grid, Container, Link, Divider } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { useTheme } from "@mui/material/styles";
import brandLogo from "../logo 3.png";

export default function OnboardingPage() {
  const theme = useTheme();
  const [form, setForm] = useState({
    shopName: "",
    address: "",
    phone: "",
    email: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    currency: "USD",
    industryKey: "electronics",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [industries, setIndustries] = useState<Array<{ key: string; name: string }>>([]);
  const [hideLogo, setHideLogo] = useState(false);
  const currencies = [
    { code: "USD", label: "USD – US Dollar" },
    { code: "ZWL", label: "ZWL – Zimbabwe Dollar" },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Load industries for selection
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const mod = await import("../services/catalog");
        const list = await mod.listIndustries();
        if (mounted) setIndustries(list);
      } catch {
        if (mounted) setIndustries([{ key: "electronics", name: "Electronics Repair" }]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      setErrorDetails([]);
      await api.post("/shops/signup", form);
      window.location.href = `/verify?email=${encodeURIComponent(form.adminEmail)}`;
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Error creating shop. Please check details and try again.";
      const details = err?.response?.data?.errors || null;
      setError(msg);
      if (details && typeof details === 'object') {
        try {
          const list = Object.values(details).filter(Boolean).map(String);
          setErrorDetails(list as string[]);
        } catch {
          setErrorDetails([]);
        }
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        backgroundImage:
          theme.palette.mode === "dark"
            ? "radial-gradient(1200px 600px at 100% -10%, rgba(37,99,235,0.10), transparent),radial-gradient(1000px 500px at -10% 110%, rgba(34,211,238,0.06), transparent)"
            : "radial-gradient(1200px 600px at 100% -10%, rgba(37,99,235,0.08), transparent),radial-gradient(1000px 500px at -10% 110%, rgba(34,211,238,0.05), transparent)",
      }}
    >
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 4, md: 8 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ xs: 3, md: 4 }}>
          <Box display="flex" alignItems="center">
            {!hideLogo && (
              <Box
                component="img"
                src={brandLogo}
                alt="Brand"
                sx={{ height: 32 }}
                onError={() => setHideLogo(true)}
              />
            )}
            {hideLogo && (
              <Typography variant="h6" fontWeight={900}>Your Brand</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.8, mr: 1, display: 'inline-block' }}>
              Already have an account?
            </Typography>
            <Link href="/login" underline="hover" fontWeight={700}>
              Sign in
            </Link>
          </Box>
        </Box>

        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
          {/* Left: Hero / Benefits */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: "100%",
                p: { xs: 2.5, md: 5 },
                borderRadius: 3,
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(14,165,233,0.08))"
                    : "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(14,165,233,0.06))",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="h3" fontWeight={900} lineHeight={1.1} gutterBottom>
                Create your shop in minutes
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
                All‑in‑one platform to run repairs, invoices, inventory and more.
              </Typography>

              <Stack spacing={2.2}>
                {["7‑day free trial — no card needed", "Invite your team instantly", "Beautiful invoices & receipts", "POS, tickets & parts tracking"].map((t) => (
                  <Box key={t} display="flex" alignItems="center">
                    <CheckCircleRounded color="primary" sx={{ mr: 1.5 }} />
                    <Typography>{t}</Typography>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 4, opacity: 0.6 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Trusted by modern repair shops. Set up takes less than 2 minutes.
              </Typography>
            </Box>
          </Grid>

          {/* Right: Form */}
          <Grid item xs={12} md={6}>
            <Paper elevation={6} sx={{ p: { xs: 3, md: 4.5 }, borderRadius: 3, height: "100%" }}>
              <Typography variant="h5" fontWeight={900} gutterBottom>
                New Shop Registration
              </Typography>
              <Typography variant="body2" sx={{ mb: 2.5, opacity: 0.8 }}>
                We’ll use this info to set up your workspace and admin account.
              </Typography>

              <Stack component="form" onSubmit={handleSubmit} spacing={2.2}>
                {error && (
                  <Alert severity="error">
                    <div>{error}</div>
                    {errorDetails.length > 0 && (
                      <ul style={{ margin: '8px 0 0 16px' }}>
                        {errorDetails.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    )}
                  </Alert>
                )}

                <Grid container spacing={2.2}>
                  <Grid item xs={12}>
                    <TextField name="shopName" label="Shop Name" value={form.shopName} onChange={handleChange} required fullWidth />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select name="industryKey" label="Industry" value={form.industryKey} onChange={handleChange} required fullWidth>
                      {industries.map((i) => (
                        <MenuItem key={i.key} value={i.key}>{i.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField select name="currency" label="Currency" value={form.currency} onChange={handleChange} required fullWidth>
                      {currencies.map((c) => (
                        <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField name="address" label="Address" value={form.address} onChange={handleChange} required fullWidth />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name="phone" label="Phone" value={form.phone} onChange={handleChange} required fullWidth />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name="email" type="email" label="Shop Email" value={form.email} onChange={handleChange} required fullWidth />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField name="adminName" label="Admin Name" value={form.adminName} onChange={handleChange} required fullWidth />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name="adminEmail" type="email" label="Admin Email" value={form.adminEmail} onChange={handleChange} required fullWidth />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="adminPassword"
                      label="Admin Password"
                      type={showPassword ? "text" : "password"}
                      value={form.adminPassword}
                      onChange={handleChange}
                      required
                      fullWidth
                      helperText={showPassword ? `Remember this password: ${form.adminPassword || "(empty)"}` : "Use at least 8 characters."}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              onClick={() => setShowPassword((s) => !s)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Button type="submit" variant="contained" size="large" sx={{ py: 1.25 }}>
                  Create Account
                </Button>

                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  By continuing you agree to our Terms and Privacy Policy.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
